import { useState, useEffect, useRef } from "react";
import * as sdk from "microsoft-cognitiveservices-speech-sdk";

const useSpeechRecognition = (t) => {
  const [listening, setListening] = useState(false);
  const [isCanceled, setIsCanceled] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState(null);
  const recognizerRef = useRef(null);
  const audioConfigRef = useRef(null);

  useEffect(() => {
    const subscriptionKey = process.env.REACT_APP_AZURE_SPEECH_KEY;
    const region = process.env.REACT_APP_AZURE_SPEECH_REGION;

    if (!subscriptionKey || !region) {
      setError(t("aiAssistant.onlineChat.credsError"));
      return;
    }

    // Initialize Azure Speech SDK
    try {
      const speechConfig = sdk.SpeechConfig.fromSubscription(
        subscriptionKey,
        region
      );
      speechConfig.speechRecognitionLanguage = "en-US";

      // Configure for continuous recognition
      speechConfig.setProperty(
        sdk.PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs,
        "10000"
      );
      speechConfig.setProperty(
        sdk.PropertyId.SpeechServiceConnection_EndSilenceTimeoutMs,
        "10000"
      );

      audioConfigRef.current = sdk.AudioConfig.fromDefaultMicrophoneInput();
      recognizerRef.current = new sdk.SpeechRecognizer(
        speechConfig,
        audioConfigRef.current
      );

      // Set up event handlers
      recognizerRef.current.sessionStarted = (s, e) => {
        setListening(true);
        setError(null);
      };

      recognizerRef.current.sessionStopped = (s, e) => {
        setListening(false);
      };

      recognizerRef.current.canceled = (s, e) => {
        setListening(false);

        if (e.reason === sdk.CancellationReason.Error) {
          setError(
            `${t("aiAssistant.onlineChat.speechError")}${e.errorDetails}`
          );
        }
      };

      // Handle interim and final results
      recognizerRef.current.recognizing = (s, e) => {};

      recognizerRef.current.recognized = (s, e) => {
        if (isCanceled) return;

        if (
          e.result.reason === sdk.ResultReason.RecognizedSpeech &&
          e.result.text
        ) {
          const finalText = e.result.text.trim();

          // Set the transcript to the final text only
          setTranscript(finalText);
        } else if (e.result.reason === sdk.ResultReason.NoMatch) {
          setTranscript("");
        }
      };
    } catch (err) {
      setError(err.message);
    }

    // Cleanup function
    return () => {
      if (recognizerRef.current) {
        recognizerRef.current.close();
        recognizerRef.current = null;
      }
      if (audioConfigRef.current) {
        audioConfigRef.current.close();
        audioConfigRef.current = null;
      }
      // Reset isCanceled state to prevent memory leaks
      setIsCanceled(false);
    };
  }, [t]);

  const startListening = async () => {
    if (!recognizerRef.current) {
      setError(t("aiAssistant.onlineChat.speechRecognitionNotInitialized"));
      return;
    }

    if (listening) {
      return;
    }

    // Reset states before starting
    setTranscript("");
    setError(null);
    setIsCanceled(false);

    try {
      // Ensure any previous session is properly stopped
      await recognizerRef.current.stopContinuousRecognitionAsync(
        () => {},
        (err) => {
          console.warn(err);
        }
      );

      // Small delay to ensure cleanup is complete
      setTimeout(async () => {
        try {
          // Start continuous recognition
          await recognizerRef.current.startContinuousRecognitionAsync(
            () => {},
            (err) => {
              setError(t("aiAssistant.onlineChat.faliedRecordingError"));
              setListening(false);
            }
          );
        } catch (err) {
          setError(t("aiAssistant.onlineChat.faliedRecordingError"));
          setListening(false);
        }
      }, 100);
    } catch (err) {
      setError(t("aiAssistant.onlineChat.faliedRecordingError"));
    }
  };

  const stopListening = async () => {
    if (!recognizerRef.current || !listening) {
      return;
    }

    try {
      await recognizerRef.current.stopContinuousRecognitionAsync(
        () => {
          setListening(false);
        },
        (err) => {
          setListening(false);
        }
      );
    } catch (err) {
      setListening(false);
    }
  };

  const clearTranscript = async () => {
    setIsCanceled(true);
    setTranscript("");

    // Properly stop and reset the recognizer
    if (recognizerRef.current && listening) {
      try {
        await recognizerRef.current.stopContinuousRecognitionAsync();
      } catch (err) {
        console.warn(err);
      }
    }

    setListening(false);
  };

  return {
    listening,
    transcript,
    error,
    startListening,
    stopListening,
    clearTranscript,
  };
};

export default useSpeechRecognition;
